import api from './client';
import { User, Rating } from '../types';

// Admin-specific types
export interface PlatformStats {
  totalUsers: number;
  verifiedUsers: number;
  totalRatings: number;
  totalSleeperAccounts: number;
  totalLeagues: number;
  newUsersToday: number;
  newRatingsToday: number;
  flaggedIpUsers: number;
}

export interface UserWithDetails extends User {
  ratingsGiven: number;
  ratingsReceived: number;
  lastLoginAt: string | null;
  lastIpAddress: string | null;
  sleeperUsername: string | null;
  password_hash?: string | null;
  auth_provider?: string;
  google_id?: string | null;
  discord_id?: string | null;
}

export interface RatingWithDetails extends Rating {
  raterEmail: string;
  raterDisplayName: string;
  ratedUsername: string;
  is_visible?: boolean;
}

export interface FlaggedUser {
  userId: string;
  email: string;
  displayName: string;
  sharedIpCount: number;
  sharedWithUsers: Array<{ id: string; email: string; displayName: string }>;
}

export interface ActivityItem {
  type: 'user_registered' | 'rating_created' | 'account_linked' | 'account_verified';
  timestamp: string;
  details: Record<string, unknown>;
}

export interface SleeperApiStats {
  currentMinuteRequests: number;
  currentMinuteStart: string;
  totalRequestsToday: number;
  todayStart: string;
  peakRequestsPerMinute: number;
  peakMinuteTime: string;
  totalRequestsAllTime: number;
  rateLimitHits: number;
  lastRequestTime: string | null;
  requestsByEndpoint: Record<string, number>;
  rateLimitPerMinute: number;
  currentUsagePercent: number;
  secondsUntilReset: number;
}

export interface QueuedRequestInfo {
  id: string;
  type: string;
  endpoint: string;
  status: string;
  priority: number;
  createdAt: string;
  startedAt?: string;
}

export interface ApiQueueStats {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  totalProcessed: number;
  averageProcessingTime: number;
  requestsPerMinute: number;
  estimatedTimeToEmpty: number;
  isProcessing: boolean;
  lastProcessedAt: string | null;
  queuedRequests: QueuedRequestInfo[];
}

export interface PaginatedResponse<T> {
  users?: T[];
  ratings?: T[];
  total: number;
  pages: number;
}

export const adminApi = {
  // Get platform statistics
  getStats: async (): Promise<PlatformStats> => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  // Get all users with pagination
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<{ users: UserWithDetails[]; total: number; pages: number }> => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  // Get user details
  getUserDetails: async (userId: string): Promise<{
    user: User;
    sleeperAccounts: unknown[];
    ratingsGiven: Rating[];
    ratingsReceived: Rating[];
    ipHistory: Array<{ ip_address: string; action: string; created_at: string }>;
  }> => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },

  // Toggle admin status
  toggleUserAdmin: async (userId: string, isAdmin: boolean): Promise<User> => {
    const response = await api.put(`/admin/users/${userId}/admin`, { isAdmin });
    return response.data;
  },

  // Toggle verified status
  toggleUserVerified: async (userId: string, isVerified: boolean): Promise<User> => {
    const response = await api.put(`/admin/users/${userId}/verified`, { isVerified });
    return response.data;
  },

  // Delete user
  deleteUser: async (userId: string): Promise<void> => {
    await api.delete(`/admin/users/${userId}`);
  },

  // Get all ratings with pagination
  getRatings: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    showHidden?: boolean;
  }): Promise<{ ratings: RatingWithDetails[]; total: number; pages: number }> => {
    const response = await api.get('/admin/ratings', { params });
    return response.data;
  },

  // Toggle rating visibility
  toggleRatingVisibility: async (ratingId: string, isVisible: boolean): Promise<Rating> => {
    const response = await api.put(`/admin/ratings/${ratingId}/visibility`, { isVisible });
    return response.data;
  },

  // Delete rating
  deleteRating: async (ratingId: string): Promise<void> => {
    await api.delete(`/admin/ratings/${ratingId}`);
  },

  // Get flagged users
  getFlaggedUsers: async (): Promise<FlaggedUser[]> => {
    const response = await api.get('/admin/flagged-users');
    return response.data;
  },

  // Get recent activity
  getActivity: async (limit?: number): Promise<ActivityItem[]> => {
    const response = await api.get('/admin/activity', { params: { limit } });
    return response.data;
  },

  // Get Sleeper API usage stats
  getSleeperApiStats: async (): Promise<SleeperApiStats> => {
    const response = await api.get('/admin/sleeper-api-stats');
    return response.data;
  },

  // Get API Queue stats
  getApiQueueStats: async (): Promise<ApiQueueStats> => {
    const response = await api.get('/admin/api-queue-stats');
    return response.data;
  },

  // Admin: Sync leagues for a sleeper account
  syncSleeperAccountLeagues: async (sleeperAccountId: string): Promise<{ success: boolean; message: string; leagues: unknown[] }> => {
    const response = await api.post(`/admin/sleeper-accounts/${sleeperAccountId}/sync-leagues`);
    return response.data;
  },

  // ── System controls ────────────────────────────────────────────────

  // Read all admin-toggleable system flags.
  getSystemSettings: async (): Promise<Record<string, unknown>> => {
    const response = await api.get('/admin/system/settings');
    return response.data;
  },

  // Update a single flag. Currently supports:
  //   - trickle_sync_enabled        (boolean)
  //   - expansion_sync_enabled      (boolean)
  //   - expansion_sync_interval_minutes (number, 1–240)
  //   - expansion_sync_batch_size       (number, 1–100)
  updateSystemSetting: async (key: string, value: unknown): Promise<{ key: string; value: unknown }> => {
    const response = await api.post(`/admin/system/settings/${key}`, { value });
    return response.data;
  },

  // Snapshot: DB size, per-table sizes, and status of both background sync jobs.
  getSystemStats: async (): Promise<SystemStats> => {
    const response = await api.get('/admin/system/stats');
    return response.data;
  },

  // Manually fire one expansion tick. The request blocks until the tick
  // completes (can take 1–2 minutes for a full 100-account batch). Bypasses
  // the `expansion_sync_enabled` pause flag so an admin can spot-check.
  //
  // Returns 409 if a tick is already running (concurrent clicks).
  runExpansionTick: async (): Promise<ExpansionTickRunResult> => {
    const response = await api.post('/admin/system/expansion-sync/run');
    return response.data;
  },

  // ── Worthless league cleanup ─────────────────────────────────────────
  // Leagues that match the same filter sync-time now applies — past-season
  // abandoned shells and single-occupant test leagues. Cleanup deletes
  // only those that have NO attached ratings (defense-in-depth).

  getWorthlessLeagues: async (): Promise<{
    count: number;
    leagues: WorthlessLeague[];
  }> => {
    const response = await api.get('/admin/worthless-leagues');
    return response.data;
  },

  cleanupWorthlessLeagues: async (): Promise<WorthlessLeagueCleanupResult> => {
    const response = await api.post('/admin/worthless-leagues/cleanup');
    return response.data;
  },
};

export interface WorthlessLeague {
  id: string;
  sleeperLeagueId: string;
  name: string;
  season: string;
  status: string;
  // Configured size of the league per Sleeper. `solo_league_config`
  // catches `< 2`; `solo_league_membership` is the higher-rosters case
  // where Sleeper says ≥2 teams but we only have 0–1 memberships.
  totalRosters: number;
  membershipCount: number;
  ratingCount: number;
  commissionerRatingCount: number;
  reason:
    | 'solo_league_config'
    | 'past_season_unfinished'
    | 'solo_league_membership';
}

export interface WorthlessLeagueCleanupResult {
  candidatesFound: number;
  deleted: number;
  skippedDueToRatings: number;
  // For `solo_league_membership` candidates we re-sync via Sleeper
  // before deleting. These two counters report the outcome of those
  // re-syncs so the admin sees the system self-healing partial syncs
  // (vs. just deleting things).
  membershipResyncRestored: number;
  membershipResyncErrors: number;
  deletedLeagueIds: string[];
}

export interface ExpansionTickRunResult {
  success: boolean;
  message: string;
  stats?: { accountsEnriched: number; errors: number };
  pendingAccounts?: number;
  elapsedMs: number;
}

export interface SystemStats {
  database: {
    totalBytes: string;
    totalPretty: string;
    tables: Array<{ name: string; bytes: string; pretty: string }>;
  };
  trickleSync: {
    enabled: boolean;
    isScheduled: boolean;
    isRunning: boolean;
    lastRunAt: string | null;
    lastRunStats: {
      accountsRefreshed: number;
      leaguesRefreshed: number;
      errors: number;
    } | null;
  };
  expansionSync: {
    enabled: boolean;
    isScheduled: boolean;
    isRunning: boolean;
    lastRunAt: string | null;
    lastRunStats: {
      accountsEnriched: number;
      errors: number;
    } | null;
    pendingAccounts: number;
    // Values that were in effect on the last tick (admin edits flow in
    // on the next tick, so this can briefly differ from what's saved).
    intervalMinutes: number;
    batchSize: number;
    // 'current' = fast but plateaus on new-account discovery in a
    // saturated network. 'all' = current + 3 prior seasons (~4×
    // API calls/shadow, but actually grows the universe).
    seasons: 'current' | 'all';
    bounds: {
      minIntervalMinutes: number;
      maxIntervalMinutes: number;
      minBatchSize: number;
      maxBatchSize: number;
      seasonsOptions: ReadonlyArray<'current' | 'all'>;
    };
  };
}

